import {DateTime} from 'luxon';

// Shared by both clients and packaged for Functions by syncAvailability.cjs.
function notificationDate(value, zone = 'UTC') {
  let date;
  if (value?.toDate) date = DateTime.fromJSDate(value.toDate());
  else if (value instanceof Date) date = DateTime.fromJSDate(value);
  else if (typeof value === 'number') date = DateTime.fromMillis(value);
  else if (typeof value?.seconds === 'number') date = DateTime.fromMillis(value.seconds * 1000);
  else date = DateTime.fromISO(String(value || ''), {zone: 'utc'});
  const localized = date.setZone(zone || 'UTC');
  return (localized.isValid ? localized : date.setZone('UTC')).setLocale('en');
}

function formatEventRange(start, end, zone = 'UTC') {
  const from = notificationDate(start, zone), to = notificationDate(end, zone);
  if (!from.isValid) return 'Schedule unavailable in the saved event';
  const date = value => value.toFormat('ccc, d LLL yyyy');
  if (!to.isValid) return `${date(from)} · ${from.toFormat('HH:mm')} → end time not saved`;
  // Keep the endpoint abbreviations when the clock changes during an event.
  // This distinguishes repeated clock times without noisy numeric offsets.
  const clock = value => value.toFormat(from.offset === to.offset ? 'HH:mm' : 'HH:mm ZZZZ');
  if (from.toISODate() === to.toISODate()) return `${date(from)} · ${clock(from)}–${clock(to)}`;
  return `${date(from)} · ${clock(from)} → ${date(to)} · ${clock(to)}`;
}

function formatEventZone(zone = 'UTC', start) {
  const date = notificationDate(start, zone);
  const validZone = date.isValid ? date.zoneName : 'UTC';
  if (validZone === 'UTC' || validZone === 'Etc/UTC') return 'UTC';
  const city = validZone.split('/').slice(1).join('/').replace(/_/g, ' ') || validZone;
  try {
    const abbreviation = new Intl.DateTimeFormat('en-US', {timeZone: validZone, timeZoneName: 'shortGeneric'})
      .formatToParts(date.toJSDate()).find(part => part.type === 'timeZoneName')?.value;
    return abbreviation && !/^GMT[+-]/.test(abbreviation) ? `${city} (${abbreviation})` : validZone;
  } catch {return validZone;}
}

function reminderLead(value) {
  const match = /^(\d+)(min|h|d)$/.exec(String(value || ''));
  if (!match) return 'Event reminder';
  const unit = {min: 'minute', h: 'hour', d: 'day'}[match[2]];
  return `${match[1]}-${unit} reminder`;
}

function attendanceRequestMessage(event) {
  const zone = event.timeZone || 'UTC';
  const start = event.eventStartUTC || event.startUTC, end = event.eventEndUTC || event.endUTC;
  return [event.teamName ? `Team: ${event.teamName}` : '', '📋 Attendance confirmation requested',
    event.eventTitle || event.eventTag || 'Calendar event', '',
    `When: ${formatEventRange(start, end, zone)}`, '',
    event.automatic ? (event.note || 'Please confirm whether you can attend. Your saved availability does not cover this event.') :
      `${event.requesterName || 'Someone'} asked you to confirm your attendance.`,
    '', `Time zone: ${formatEventZone(zone, start)}`, event.note && !event.automatic ? `Note: ${event.note}` : '']
    .filter((line, index, lines) => line || (index > 0 && lines[index - 1])).join('\n').trim();
}

export {formatEventRange, formatEventZone, reminderLead, attendanceRequestMessage};
